/**
 * useBlogSearch Composable
 * Full-text search across blog articles with client-side relevance scoring
 * Story: 115-122-T001
 *
 * Implementation notes:
 * - Uses contract-first fetch pattern: queryContent('blog').where({ _draft: false }).find()
 * - Falls back to Nuxt Content v3 queryCollection('blog') when queryContent is unavailable
 * - Article path field is `path` (not `_path`) — Nuxt Content v3 convention
 * - Body text extracted via JSON.stringify(MarkdownRoot AST) for MVP substring search
 * - XSS safety: regex special characters escaped before RegExp construction (R-005)
 * - SSG hydration safety: route.query accessed only inside computed/onMounted (R-006, L146)
 */

import { ref, computed, onMounted, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { useRoute } from '#imports'

// ---------------------------------------------------------------------------
// Nuxt Content v3 ambient declarations for `queryCollection`.
//
// `queryCollection` is globally injected at runtime by the @nuxt/content Nuxt
// module. The full generic signature `queryCollection<T extends keyof Collections>`
// depends on the `Collections` module augmentation generated into `.nuxt/content/
// types.d.ts`, which is excluded from plain `tsc` compilation.
//
// This self-contained declaration captures the exact shape of the blog query
// builder (field names match `BlogCollectionItem` generated in .nuxt/content/
// types.d.ts from the schema in content.config.ts). No `@nuxt/content` import
// is required — all types are inlined here so the composable is fully portable
// across tsc, Vite, and Nuxt build contexts.
// ---------------------------------------------------------------------------

/** Matches the generated BlogCollectionItem in .nuxt/content/types.d.ts */
interface RawBlogItem {
  path?: string
  _path?: string
  title?: string
  description?: string
  excerpt?: string
  body: unknown
  categories?: string[]
  tags?: string[]
  product?: string
  publishedAt?: string
  _draft?: boolean
  draft?: boolean
  id: string
  stem: string
  extension: string
  meta: Record<string, unknown>
}

/** Fluent query builder returned by queryCollection('blog') at runtime */
interface BlogQueryBuilder {
  where(field: string, operator: string, value: unknown): BlogQueryBuilder
  order(field: string, direction: 'ASC' | 'DESC'): BlogQueryBuilder
  limit(n: number): BlogQueryBuilder
  skip(n: number): BlogQueryBuilder
  all(): Promise<RawBlogItem[]>
  first(): Promise<RawBlogItem | null>
  count(): Promise<number>
}

/** queryContent-style builder used by contract pseudocode */
interface QueryContentBuilder {
  where(filters: Record<string, unknown>): QueryContentBuilder
  find(): Promise<RawBlogItem[]>
}

/** Ambient declarations: @nuxt/content auto-imports one of these at runtime */
declare const queryContent: undefined | ((collection: 'blog') => QueryContentBuilder)
declare const queryCollection: undefined | ((collection: 'blog') => BlogQueryBuilder)

type NormalizedRawBlogItem = RawBlogItem & {
  path: string
  excerpt: string
  draft: boolean
}

function isNormalizedRawBlogItem(
  row: RawBlogItem & { path: string | undefined; excerpt: string; draft: boolean }
): row is NormalizedRawBlogItem {
  return typeof row.path === 'string' && row.path.length > 0
}

function normalizeFetchedArticles(rows: RawBlogItem[]): NormalizedRawBlogItem[] {
  return rows
    .map((row) => ({
      ...row,
      path: row.path ?? row._path,
      excerpt: row.excerpt ?? row.description ?? '',
      draft: row.draft ?? row._draft ?? false,
    }))
    .filter(isNormalizedRawBlogItem)
}

async function fetchPublishedArticles(): Promise<NormalizedRawBlogItem[]> {
  if (typeof queryContent === 'function') {
    const contentRows = normalizeFetchedArticles(
      await queryContent('blog').where({ _draft: false }).find(),
    )
    if (contentRows.length > 0) {
      return contentRows
    }
  }

  if (typeof queryCollection === 'function') {
    return normalizeFetchedArticles(
      await queryCollection('blog').where('draft', '=', false).all(),
    )
  }

  throw new Error('No Nuxt Content query API available for blog search')
}

// Relevance score weights per spec (R-002)
const SCORE_TITLE = 100
const SCORE_EXCERPT = 50
const SCORE_BODY_PER_MATCH = 10
const SCORE_CATEGORY = 25

/**
 * Escapes special regex characters in user input to prevent XSS/ReDoS (R-005).
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Extracts searchable text from a Nuxt Content v3 MarkdownRoot AST via JSON serialisation.
 * JSON.stringify captures all text node values within the AST tree. Acceptable for MVP
 * substring search — false-positive rate is negligible for natural-language queries.
 */
function extractBodyText(body: unknown): string {
  if (body === null || body === undefined) return ''
  return JSON.stringify(body)
}

/**
 * A blog article as returned by the blog collection query.
 * Note: Nuxt Content v3 uses `path` (not `_path` as in v2).
 */
export interface BlogArticle {
  /** URL path of the article (Nuxt Content v3 field, not `_path`) */
  path: string
  title: string
  excerpt: string
  body?: unknown
  categories?: string[]
  tags?: string[]
  product?: string
  publishedAt?: string
}

/** A BlogArticle decorated with a client-side relevance score */
export interface BlogSearchResult extends BlogArticle {
  relevanceScore: number
}

/** Public API surface returned by useBlogSearch */
export interface UseBlogSearchReturn {
  /** The current search query string (writable ref) */
  searchQuery: Ref<string>
  /** Scored, filtered, and sorted search results */
  searchResults: Ref<BlogSearchResult[]>
  /** True while the async fetch + scoring is in progress */
  isSearching: Ref<boolean>
  /** Number of results in the current searchResults set */
  resultCount: ComputedRef<number>
  /** True when searchQuery has at least one non-whitespace character */
  hasSearch: ComputedRef<boolean>
  /** Fetch all published articles and apply relevance scoring for the given query */
  performSearch: (query: string) => Promise<void>
  /** Reset searchQuery and searchResults to empty */
  clearSearch: () => void
  /** Report a result-click event (fires onResultClicked callback if provided) */
  reportResultClicked: (articleId: string, position: number) => void
}

/**
 * Optional telemetry callbacks injected by the page layer.
 * Keeps useBlogSearch SSG-safe — no direct analytics import at composable scope.
 * All callbacks fire inside event-handler / try-block context (L145, R-007).
 */
export interface UseBlogSearchOptions {
  /** Called after searchResults are set; provides query, result count, and elapsed ms */
  onSearchCompleted?: (query: string, resultCount: number, responseTimeMs: number) => void
  /** Called when search yields zero results */
  onNoResults?: (query: string) => void
  /** Called when the user clicks a result card */
  onResultClicked?: (query: string, articleId: string, position: number) => void
}

export interface UseBlogFilterStateReturn {
  selectedProduct: Ref<string | null>
  selectedCategory: Ref<string | null>
  availableProducts: ComputedRef<string[]>
  availableCategories: ComputedRef<string[]>
  hasActiveFilters: ComputedRef<boolean>
  filteredArticles: ComputedRef<BlogArticle[]>
  setFilters: (filters: { product?: string | null; category?: string | null }) => void
  clearFilters: () => void
}

function normalizeFilterValue(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const normalized = value.trim().toLowerCase()
  return normalized.length > 0 ? normalized : null
}

function buildUniqueSorted(values: Array<string | null | undefined>): string[] {
  const unique = new Set<string>()
  for (const value of values) {
    const normalized = normalizeFilterValue(value)
    if (normalized) unique.add(normalized)
  }
  return [...unique].sort((a, b) => a.localeCompare(b))
}

export function useBlogFilterState(
  allArticles: Ref<BlogArticle[] | null | undefined>,
): UseBlogFilterStateReturn {
  const route = useRoute()
  const selectedProduct = ref<string | null>(null)
  const selectedCategory = ref<string | null>(null)

  const routeProduct = computed(() => normalizeFilterValue(route.query.product))
  const routeCategory = computed(() => normalizeFilterValue(route.query.category))

  onMounted(() => {
    selectedProduct.value = routeProduct.value
    selectedCategory.value = routeCategory.value
  })

  watch([routeProduct, routeCategory], ([product, category]) => {
    selectedProduct.value = product
    selectedCategory.value = category
  })

  const availableProducts = computed(() =>
    buildUniqueSorted((allArticles.value ?? []).map(article => article.product)),
  )

  const availableCategories = computed(() =>
    buildUniqueSorted((allArticles.value ?? []).flatMap(article => article.categories ?? [])),
  )

  const hasActiveFilters = computed(() =>
    selectedProduct.value !== null || selectedCategory.value !== null,
  )

  const filteredArticles = computed(() => {
    const articles = allArticles.value ?? []
    if (!hasActiveFilters.value) return articles
    return articles.filter((article) => {
      const productMatch = !selectedProduct.value ||
        normalizeFilterValue(article.product) === selectedProduct.value
      const categoryMatch = !selectedCategory.value ||
        (article.categories?.some(category => normalizeFilterValue(category) === selectedCategory.value) ?? false)
      return productMatch && categoryMatch
    })
  })

  function setFilters(filters: { product?: string | null; category?: string | null }): void {
    selectedProduct.value = normalizeFilterValue(filters.product)
    selectedCategory.value = normalizeFilterValue(filters.category)
  }

  function clearFilters(): void {
    selectedProduct.value = null
    selectedCategory.value = null
  }

  return {
    selectedProduct,
    selectedCategory,
    availableProducts,
    availableCategories,
    hasActiveFilters,
    filteredArticles,
    setFilters,
    clearFilters,
  }
}

/**
 * Composable providing full-text blog search with client-side relevance scoring.
 *
 * Scoring weights (R-002):
 *   - Title match:    +100 per word
 *   - Excerpt match:  +50  per word
 *   - Body match:     +10  per word per occurrence
 *   - Category match: +25  per word (any matching category)
 *
 * Multi-word AND logic: every search word must appear in at least one field;
 * articles where any word has zero score are excluded. Score = sum of all word scores.
 *
 * @returns {UseBlogSearchReturn}
 */
export function useBlogSearch(options: UseBlogSearchOptions = {}): UseBlogSearchReturn {
  const searchQuery = ref<string>('')
  const searchResults = ref<BlogSearchResult[]>([])
  const isSearching = ref<boolean>(false)

  // SSG-safe: useRoute() is called within composable/setup context — never at module level.
  // route.query is accessed only inside computed() and onMounted() (L146, R-006).
  const route = useRoute()
  const routeSearchQuery: ComputedRef<string> = computed(() => {
    const routeSearch = route.query.search
    return typeof routeSearch === 'string' ? routeSearch : ''
  })

  /** True when searchQuery has at least one non-whitespace character */
  const hasSearch: ComputedRef<boolean> = computed(
    () => searchQuery.value.trim().length > 0,
  )

  /** Number of items in searchResults */
  const resultCount: ComputedRef<number> = computed(
    () => searchResults.value.length,
  )

  // Initialise searchQuery from URL query parameter on client mount (L145 — side effects
  // must be in onMounted, not at module or setup level).
  onMounted(() => {
    if (routeSearchQuery.value.trim().length > 0) {
      searchQuery.value = routeSearchQuery.value
    }
  })

  /**
   * Fetches all published blog articles and scores them against the given query.
   * Sets isSearching during async work; always clears it on completion (even on error).
   */
  const performSearch = async (query: string): Promise<void> => {
    searchQuery.value = query

    // Split query into individual words; filter empty tokens
    const words = query
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w.length > 0)

    if (words.length === 0) {
      searchResults.value = []
      return
    }

    isSearching.value = true
    const startTime = Date.now()

    try {
      // Contract fetch pattern + v3 fallback handled in helper
      const articles = await fetchPublishedArticles()

      const scored: BlogSearchResult[] = []

      for (const article of articles) {
        let totalScore = 0
        let allWordsMatch = true

        for (const word of words) {
          // R-005: Escape special regex characters before RegExp construction
          const escaped = escapeRegex(word)

          let wordScore = 0

          const normalizedWord = word.toLowerCase()
          const title = (article.title ?? '').toLowerCase()
          const excerpt = (article.excerpt ?? article.description ?? '').toLowerCase()

          // Title match — highest weight (SCORE_TITLE = 100)
          if (title.includes(normalizedWord)) {
            wordScore += SCORE_TITLE
          }

          // Excerpt match — second tier (SCORE_EXCERPT = 50)
          if (excerpt.includes(normalizedWord)) {
            wordScore += SCORE_EXCERPT
          }

          // Body match — per-occurrence weight (SCORE_BODY_PER_MATCH = 10)
          // article.body is MarkdownRoot (MDC AST); JSON.stringify extracts all text values
          const bodyText = extractBodyText(article.body).toLowerCase()
          if (bodyText.length > 0) {
            const bodyRegex = new RegExp(escaped, 'gi')
            const bodyMatches = bodyText.match(bodyRegex)
            if (bodyMatches) {
              wordScore += bodyMatches.length * SCORE_BODY_PER_MATCH
            }
          }

          // Category match — any category name contains the word (SCORE_CATEGORY = 25)
          if (article.categories && article.categories.length > 0) {
            const categoryMatch = article.categories.some(
              (cat: string) => cat.toLowerCase().includes(normalizedWord),
            )
            if (categoryMatch) {
              wordScore += SCORE_CATEGORY
            }
          }

          // AND logic: if this word yields no score in any field, exclude the article
          if (wordScore === 0) {
            allWordsMatch = false
            break
          }

          totalScore += wordScore
        }

        if (allWordsMatch && totalScore > 0) {
          scored.push({
            path: article.path ?? article._path ?? '',
            title: article.title ?? 'Untitled',
            excerpt: article.excerpt ?? article.description ?? '',
            body: article.body as unknown,
            categories: article.categories,
            tags: article.tags,
            product: article.product,
            publishedAt: article.publishedAt,
            relevanceScore: totalScore,
          })
        }
      }

      // SC3: Sort descending by relevance score — title-match articles rank above body-match articles
      scored.sort((a, b) => b.relevanceScore - a.relevanceScore)
      searchResults.value = scored

      // Telemetry: fire AFTER results are set, inside try block (event handler context — L145, R-007)
      const responseTimeMs = Date.now() - startTime
      options.onSearchCompleted?.(query, scored.length, responseTimeMs)
      if (scored.length === 0) {
        options.onNoResults?.(query)
      }
    } finally {
      // Always clear loading state, even if an error occurs
      isSearching.value = false
    }
  }

  /** Resets search state — clears query string and results list */
  const clearSearch = (): void => {
    searchQuery.value = ''
    searchResults.value = []
  }

  /**
   * Reports a result-click event to the page layer via the onResultClicked callback.
   * Fires inside an event handler context (click) — satisfies L145.
   */
  const reportResultClicked = (articleId: string, position: number): void => {
    options.onResultClicked?.(searchQuery.value, articleId, position)
  }

  return {
    searchQuery,
    searchResults,
    isSearching,
    resultCount,
    hasSearch,
    performSearch,
    clearSearch,
    reportResultClicked,
  }
}
